// Importing necessary modules and dependencies
import * as THREE from 'three';
import EventDispatcher from '../common/EventDispatcher';
import { Global } from '../common/global';
import * as CANNON from 'cannon';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import gsap from 'gsap';
// Definition of the GameScene class
export default class GameScene {
  // Constructor takes a scene and camera as parameters
  constructor(scene, camera) {
    this.scene = scene;
    this.camera = camera;

    this.onEvents = this.onEvents.bind(this);

    // Keep track of bodies and constraints we add so we can remove them cleanly
    this._bodiesAdded = [];
    this._constraintsAdded = [];
  }

  // Initialization method for setting up event listeners and initial state
  init(gui) {
    this.gui = gui;
    this.events = EventDispatcher.getObj();

    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents);
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents);
    this.events.addEventListener('GAME_EVENTS', this.onEvents);
  }
  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'create_scene':
        this.createScene();
        break;
      case 'update':
        this.update(data['message']['data']);
        break;
      case 'start_time':
        // this.startTimer();
        break;
      case 'update_post_feature':
        this.updatePostFeature();
        break;
      case 'defend':
        this.showDefendEffects();
        break;
      case 'assign_position':
        this.stopDefendAnim();
        break;
      case 'dispose':
        this.dispose();
        break;
    }
  }

  // helper: copy cannon body transform into three mesh (keeps visual/physics aligned)
  syncMeshToBody(mesh, body) {
    if (!mesh || !body) return;
    mesh.position.copy(body.position);
    mesh.quaternion.set(
      body.quaternion.x,
      body.quaternion.y,
      body.quaternion.z,
      body.quaternion.w
    );
  }

  createScene() {
    this.addStadium();
  }

  // Method to add a track to the scene based on a given index
  addStadium() {
    // Cloning the road asset from global assets
    this.stadium = Global.assets['glbs'][`stadium`]['asset'].scene.clone();
    this.scene.add(this.stadium);
    this.stadium.scale.set(
      Global.scaleFact,
      Global.scaleFact,
      Global.scaleFact
    );

    // this.stadium.visible = false;
    this.cameraFlash = Global.assets['glbs'][`flash`]['asset'].scene;
    this.scene.add(this.cameraFlash);
    this.cameraFlash.position.set(-0.85, 0, -52.7 * Global.scaleFact * 1.22);
    this.cameraFlash.rotation.y = Math.PI;
    this.cameraFlash.scale.set(
      Global.scaleFact,
      Global.scaleFact,
      Global.scaleFact
    );
    this.cameraFlash.traverse((flash) => {
      if (flash.isMesh) {
        flash.envMapIntensity = 2;
        flash.envMap = Global.envMap;

        this.flashScale = {
          value: 5,
        };
        flash.userData.flashScale = this.flashScale;
        flash.scale.set(0, 0, 0);

        gsap.to(this.flashScale, {
          value: 0,
          yoyo: false,
          delay: Math.random(),
          onUpdate: function (_scale) {
            flash.scale.set(_scale.value, _scale.value, _scale.value);
          }.bind(this, this.flashScale),
          repeatDelay: Math.max(0.5, Math.random()) * 4,
          repeat: -1,
          duration: 0.1,
        });
      }
    });

    // this.flag.scale.set(0.6, 0.6, 0.6);

    this.flag = Global.assets['glbs'][`flag`]['asset'].scene;
    this.scene.add(this.flag);
    this.flag.scale.set(0.6, 0.6, 0.6);

    // use SkeletonUtils.clone so skeletons, skinnedMeshes, bone bindings are copied correctly

    this.scene.add(this.flag);
    this.flag.traverse((item) => {
      if (item.isMesh) {
        item.material.envMapIntensity = 0;
      }
    });

    this.flagMixer = new THREE.AnimationMixer(this.flag);
    this.flagWaving = this.flagMixer.clipAction(
      THREE.AnimationClip.findByName(
        Global.assets['glbs'][`flag`]['asset']['animations'],
        'Animation'
      )
    );
    this.flagWaving.play();

    this.flag2 = SkeletonUtils.clone(this.flag);
    this.flag2.position.x += 10; // move it so they don't overlap
    this.scene.add(this.flag2);
    this.flagMixer2 = new THREE.AnimationMixer(this.flag2);
    this.flag2.scale.set(0.8, 0.8, 0.8);
    this.flagWaving2 = this.flagMixer2.clipAction(
      THREE.AnimationClip.findByName(
        Global.assets['glbs'][`flag`]['asset']['animations'],
        'Animation'
      )
    );
    this.flagWaving2.time += 0.3;

    this.flagWaving2.play();

    this.flag3 = SkeletonUtils.clone(this.flag);
    this.flag3.position.x += 10;
    this.scene.add(this.flag3);
    this.flagMixer3 = new THREE.AnimationMixer(this.flag3);

    this.flagWaving3 = this.flagMixer3.clipAction(
      THREE.AnimationClip.findByName(
        Global.assets['glbs'][`flag`]['asset']['animations'],
        'Animation'
      )
    );
    this.flagWaving3.time += 0.5;
    this.flag3.scale.set(0.5, 0.5, 0.5);
    this.flagWaving3.play();

    this.flag4 = SkeletonUtils.clone(this.flag);
    this.flag4.position.x += 10;
    this.scene.add(this.flag4);
    this.flagMixer4 = new THREE.AnimationMixer(this.flag4);

    this.flagWaving4 = this.flagMixer4.clipAction(
      THREE.AnimationClip.findByName(
        Global.assets['glbs'][`flag`]['asset']['animations'],
        'Animation'
      )
    );
    this.flagWaving4.time += 0.7;
    this.flag4.scale.set(0.5, 0.5, 0.5);
    this.flagWaving4.play();

    this.flag.position.set(10, 1.5, -60);
    this.flag2.position.set(-10, 3.5, -60);
    this.flag3.position.set(-25, 2.5, -40);
    this.flag4.position.set(25, 2.5, -40);

    // this.stadium.visible = false;
    this.stadium.position.set(-0.85, 0, -52.7 * Global.scaleFact * 1.22);
    this.stadium.rotation.y = Math.PI;

    // this.stadiumFade.position.set(-0.85, 0, -52.7 * Global.scaleFact * 1.22);
    // this.stadiumFade.rotation.y = Math.PI;

    // this.stadium.cast;
    this.stadium.traverse((item) => {
      if (item.isMesh) {
        item.receiveShadow = true;

        //tex.anisotropy
        // if (item.material.name.indexOf('football_field') !== -1) {
        //   item.material.map.anisotropy = 10;
        //   item.material.map.needsUpdate = true;
        // }
        if (
          item.name === 'CrowdTexture004_3' ||
          item.name === 'CrowdTexture004_4'
        ) {
          // item.visible = false;

          item.material.transparent = true;
          item.material.opacity = 0.35;
        }
        // item.material.side = THREE.DoubleSide;

        if (item.material.name === 'Material.001' && !this.postMesh) {
          item.material.envMapIntensity = 1;
          item.material.transparent = true;
          item.material.opacity = 1;
          item.material.metalness = 1;
          item.material.roughness = 0;
          this.postMesh = item;
        }
      }
    });

    // ---------- postNet (visual) ----------
    // this.postNet = new THREE.Mesh(
    //   new THREE.PlaneGeometry(2.7 * 1.9, 2.7),
    //   new THREE.MeshBasicMaterial({
    //     map: Global.assets['textures'][`net`]['asset'],
    //     transparent: true,
    //     side: THREE.DoubleSide,
    //     // color: 'red',
    //   })
    // );
    // // add to scene (was commented out previously)
    // this.scene.add(this.postNet);
    // this.postNet.scale.set(
    //   Global.scaleFact,
    //   Global.scaleFact,
    //   Global.scaleFact
    // );
    // this.postNet.visible = false;
    // this.postNet.position.set(-0.85, 1.3, 0.85 * Global.scaleFact);

    // Create a matching physics body (thin box) for postNet
    // Keep this as a static body (mass = 0) so it acts as a collision target

    this.stadium.traverse((child) => {
      if (!child.material) return false;
      // child.material.depthTest = false;
      child.material.envMap = Global.envMap;
      // child.material.envMapIntensity = 0;
    });

    const floorShape = new CANNON.Plane(); // Infinite plane shape
    const floorBody = new CANNON.Body({
      mass: 0, // Mass of 0 makes the body static
      shape: floorShape,
      material: Global.floorMaterial, // Use the same material as the ball
    });
    floorBody.position.set(0, 0, 0);
    floorBody.quaternion.setFromAxisAngle(
      new CANNON.Vec3(1, 0, 0),
      -Math.PI / 2
    );
    if (this.scene.world) {
      this.scene.world.addBody(floorBody);
      this._bodiesAdded.push(floorBody);
    }

    const backSize = {
      w: 4.3 * Global.scaleFact,
      h: 1.85 * Global.scaleFact,
    };
    const sideSize = {
      w: 1.8 * Global.scaleFact,
      h: 1.3 * Global.scaleFact,
    };
    const topSize = {
      w: 4.4 * Global.scaleFact,
      h: 1.3 * Global.scaleFact,
    };
    const planePhysicsHeight = 0.5;
    // Create PlaneGeometries and rotate them so their local axes better match Cannon bodies:
    // PlaneGeometry default normal is +Z; we want vertical planes so rotateX(-PI/2)
    const planeGeometryBack = new THREE.PlaneGeometry(backSize.w, backSize.h);
    planeGeometryBack.rotateX(-Math.PI / 2);

    const planeGeometrySide = new THREE.PlaneGeometry(sideSize.w, sideSize.h);
    // rotate side planes so their normals point in expected direction for vertical side planes
    // rotateX to make them vertical; rotateY will be applied through sync if needed
    planeGeometrySide.rotateX(-Math.PI / 2);

    const planeGeometryTop = new THREE.PlaneGeometry(topSize.w, topSize.h);
    // top is often horizontal — but we rotate to align consistently
    planeGeometryTop.rotateX(-Math.PI / 2);

    const planeMaterial = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      side: THREE.DoubleSide,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      opacity: 0,
    });
    const planeBack = new THREE.Mesh(planeGeometryBack, planeMaterial);
    this.scene.add(planeBack);

    const planeLeft = new THREE.Mesh(planeGeometrySide, planeMaterial);
    this.scene.add(planeLeft);

    const planeRight = new THREE.Mesh(planeGeometrySide, planeMaterial);
    this.scene.add(planeRight);

    const planeTop = new THREE.Mesh(planeGeometryTop, planeMaterial);
    this.scene.add(planeTop);

    const planeBottom = new THREE.Mesh(planeGeometryTop, planeMaterial);
    this.scene.add(planeBottom);

    this.planeTop = planeTop;

    // Back body — use half extents matching plane size (w/2, depth/2, h/2)
    const shapeBack = new CANNON.Box(
      new CANNON.Vec3(backSize.w / 2, planePhysicsHeight / 2, backSize.h / 2)
    );
    this.backBody = new CANNON.Body({
      mass: 0,
      material: Global.netMaterial,
    });
    this.backBody.addShape(shapeBack);
    this.backBody.position.set(
      -0.9,
      0.9 * Global.scaleFact,
      0.9 * Global.scaleFact - 0.15
    );
    // normalize axis and set rotation
    {
      const axis = new CANNON.Vec3(1, 0, 0);
      axis.normalize();
      this.backBody.quaternion.setFromAxisAngle(axis, -Math.PI / 2);
    }
    if (this.scene.world) {
      this.scene.world.addBody(this.backBody);
      this._bodiesAdded.push(this.backBody);
    }

    // Left body
    const shapeLeft = new CANNON.Box(
      new CANNON.Vec3(sideSize.w / 2, planePhysicsHeight / 2, sideSize.h / 2)
    );
    this.leftBody = new CANNON.Body({
      mass: 0,
      material: Global.netMaterial,
    });
    this.leftBody.addShape(shapeLeft);
    this.leftBody.position.set(
      1.3 * Math.pow(Global.scaleFact, 1.4),
      0.9 * Global.scaleFact,
      0.2 * Global.scaleFact - 0.25
    );
    // normalize diagonal axis if intentional
    {
      const axis = new CANNON.Vec3(1, 1, 0);
      axis.normalize();
      this.leftBody.quaternion.setFromAxisAngle(axis, Math.PI);
    }
    if (this.scene.world) {
      this.scene.world.addBody(this.leftBody);
      this._bodiesAdded.push(this.leftBody);
    }

    // Right body
    const shapeRight = new CANNON.Box(
      new CANNON.Vec3(sideSize.w / 2, planePhysicsHeight / 2, sideSize.h / 2)
    );
    this.rightBody = new CANNON.Body({
      mass: 0,
      material: Global.netMaterial,
    });
    this.rightBody.addShape(shapeRight);
    this.rightBody.position.set(
      1.3 * Math.pow(Global.scaleFact, 1.43) - 4.38 * Global.scaleFact,
      0.9 * Global.scaleFact,
      0.2 * Global.scaleFact - 0.25
    );
    {
      const axis = new CANNON.Vec3(1, 1, 0);
      axis.normalize();
      this.rightBody.quaternion.setFromAxisAngle(axis, Math.PI);
    }
    if (this.scene.world) {
      this.scene.world.addBody(this.rightBody);
      this._bodiesAdded.push(this.rightBody);
    }

    // Top body
    const shapeTop = new CANNON.Box(
      new CANNON.Vec3(topSize.w / 2, planePhysicsHeight / 2, topSize.h / 2)
    );
    this.topBody = new CANNON.Body({
      mass: 0,
      material: Global.netMaterial,
    });
    this.topBody.addShape(shapeTop);
    this.topBody.position.set(
      -0.9,
      1.8 * Global.scaleFact,
      0.2 * Global.scaleFact - 0.25
    );
    if (this.scene.world) {
      this.scene.world.addBody(this.topBody);
      this._bodiesAdded.push(this.topBody);
    }

    // Bottom body (thin)
    const shapeBottom = new CANNON.Box(
      new CANNON.Vec3(topSize.w / 2, 0.001, topSize.h / 2)
    );
    this.bottomBody = new CANNON.Body({
      mass: 0,
      material: Global.netMaterial2,
    });
    this.bottomBody.addShape(shapeBottom);
    this.bottomBody.position.set(
      -0.88,
      0 * Global.scaleFact,
      0.2 * Global.scaleFact - 0.25
    );
    if (this.scene.world) {
      this.scene.world.addBody(this.bottomBody);
      this._bodiesAdded.push(this.bottomBody);
    }

    // Sync visual planes to bodies (copy transforms from cannon to three)
    planeBack.position.copy(this.backBody.position);
    this.syncMeshToBody(planeBack, this.backBody);

    planeLeft.position.copy(this.leftBody.position);
    this.syncMeshToBody(planeLeft, this.leftBody);

    planeRight.position.copy(this.rightBody.position);
    this.syncMeshToBody(planeRight, this.rightBody);

    planeTop.position.copy(this.topBody.position);
    this.syncMeshToBody(planeTop, this.topBody);

    planeBottom.position.copy(this.bottomBody.position);
    this.syncMeshToBody(planeBottom, this.bottomBody);

    // -- Optional debug helpers: uncomment to visualize local axes and debug box alignment --
    // const showDebug = false;
    // if (showDebug) {
    //   const addAxesAt = (meshOrPos, quat) => {
    //     const ax = new THREE.AxesHelper(0.6);
    //     if (meshOrPos.position) ax.position.copy(meshOrPos.position);
    //     else ax.position.copy(meshOrPos);
    //     if (quat) ax.quaternion.copy(quat);
    //     this.scene.add(ax);
    //   };
    //
    //   // axes at bodies
    //   const bodies = [this.backBody, this.leftBody, this.rightBody, this.topBody, this.bottomBody];
    //   bodies.forEach((b) => {
    //     const proxy = new THREE.Object3D();
    //     proxy.position.copy(b.position);
    //     proxy.quaternion.set(b.quaternion.x, b.quaternion.y, b.quaternion.z, b.quaternion.w);
    //     this.scene.add(proxy);
    //     proxy.add(new THREE.AxesHelper(0.6));
    //   });
    //
    //   // debug boxes to match cannon extents
    //   const debugBack = new THREE.Mesh(new THREE.BoxGeometry(backSize.w, planePhysicsHeight, backSize.h), new THREE.MeshNormalMaterial({ wireframe: true }));
    //   debugBack.position.copy(this.backBody.position);
    //   debugBack.quaternion.set(this.backBody.quaternion.x, this.backBody.quaternion.y, this.backBody.quaternion.z, this.backBody.quaternion.w);
    //   this.scene.add(debugBack);
    // }
  }
  stopDefendAnim() {
    this.cameraFlash.traverse((flash, index) => {
      if (flash.isMesh) {
        this[`defendFlashTwn${index}`] && this[`defendFlashTwn${index}`].kill();
      }
    });
  }
  showDefendEffects() {
    this.cameraFlash.traverse((flash, index) => {
      if (flash.isMesh) {
        this.flashScale = {
          value: 5,
        };
        this[`defendFlashTwn${index}`] = gsap.to(this.flashScale, {
          value: 0,
          yoyo: false,
          delay: Math.random(),
          onUpdate: function (_scale) {
            flash.scale.set(_scale.value, _scale.value, _scale.value);
          }.bind(this, this.flashScale),
          // repeatDelay: Math.max(0.5, Math.random()) * 4,
          repeat: 4,
          duration: 0.125,
        });
      }
    });
  }
  // Update method for scene adjustments
  update(data) {
    if (this.flagMixer) {
      this.flagMixer.update(Global.delta * 0.0025);
      this.flagMixer2.update(Global.delta * 0.0025);
      this.flagMixer3.update(Global.delta * 0.0025);
      this.flagMixer4.update(Global.delta * 0.0025);
    }
  }
  updatePostFeature() {
    gsap.delayedCall(
      Global.gameTry == 1 ? Global.cameraPanTime * 0.5 : 0,
      () => {
        this.postMesh.material.envMapIntensity = 10;
        this.postMesh.material.depthWrite = false;
        this.postMesh.material.blending = THREE.MultiplyBlending;
        this.postMesh.material.opacity = 0.9;
      }
    );
  }
  dispose() {
    // Dispose Three.js objects
    this.cameraFlash.traverse((flash) => {
      if (flash.isMesh) {
        gsap.killTweensOf(flash.userData.flashScale);
        if (flash.geometry) flash.geometry.dispose();
        if (flash.material) {
          if (Array.isArray(flash.material)) {
            flash.material.forEach((material) => material.dispose());
          } else {
            flash.material.dispose();
          }
        }
      }
    });
    this.scene.remove(this.cameraFlash);
    this.cameraFlash = null;

    if (this.stadium) {
      this.stadium.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
      this.scene.remove(this.stadium);
      this.stadium = null;
    }
    if (this.flag) {
      this.flag.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
      this.scene.remove(this.flag);
      this.scene.remove(this.flag2);
      this.scene.remove(this.flag3);
      this.scene.remove(this.flag4);
      this.flag = null;
      this.flag2 = null;
      this.flag3 = null;
      this.flag4 = null;
    }

    // Dispose postNet mesh/material if present
    // if (this.postNet) {
    //   if (this.postNet.geometry) this.postNet.geometry.dispose();
    //   if (this.postNet.material) {
    //     if (Array.isArray(this.postNet.material)) {
    //       this.postNet.material.forEach((m) => m.dispose());
    //     } else {
    //       this.postNet.material.dispose();
    //     }
    //   }
    //   this.scene.remove(this.postNet);
    //   this.postNet = null;
    // }
    // Null references to bodies we keep
    if (this.postBody) {
      this.postBody = null;
    }

    // Remove only bodies we added earlier
    if (this.scene.world) {
      // Remove bodies we added
      for (let b of this._bodiesAdded) {
        try {
          this.scene.world.removeBody(b);
        } catch (e) {
          // ignore remove errors
        }
      }
      this._bodiesAdded.length = 0;

      // Remove constraints we added (if any)
      for (let c of this._constraintsAdded) {
        try {
          this.scene.world.removeConstraint(c);
        } catch (e) {
          // ignore
        }
      }
      this._constraintsAdded.length = 0;
    }

    // Remove event listeners (guard events)
    if (this.events) {
      this.events.removeEventListener('GAMESCENE_EVENTS', this.onEvents);
      this.events.removeEventListener('PLAYER_EVENTS', this.onEvents);
      this.events.removeEventListener('GAME_EVENTS', this.onEvents);
    }

    // Force garbage collection (for Chrome, mostly)
    if (window.gc) {
      window.gc();
    }
  }
}
